import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Container, Typography } from '@material-ui/core';

import { RailzButton } from '@railzai/railz-uikit-react';

import Logo from '../../components/logo';

import Title from '../../components/title';

import Loading from '../../components/loading';

import useStyle from './style';

import SetupAccountForm from './setup-account-form';

import { View } from 'components';
import { AuthService, CustomerService } from 'services';

import { showSnackbar } from 'helpers/common.helper';

import useQuerySearch from 'hooks/use-query-search';

import LayoutImage from 'pages/layout-image/layout-image.page';

const VERIFY_USER_COOLDOWN_MS = 3000;
let verifyUserLastCalledAt = 0;
let verifyUserPendingRequest: Promise<any> | null = null;

const verifyUserWithCooldown = (token: string | null): Promise<any> => {
  const now = Date.now();

  if (verifyUserPendingRequest && now - verifyUserLastCalledAt < VERIFY_USER_COOLDOWN_MS) {
    return verifyUserPendingRequest;
  }

  if (now - verifyUserLastCalledAt < VERIFY_USER_COOLDOWN_MS) {
    return Promise.reject(new Error('VERIFY_USER_COOLDOWN_ACTIVE'));
  }

  verifyUserLastCalledAt = now;
  verifyUserPendingRequest = CustomerService.verifyUser(token).finally(() => {
    verifyUserPendingRequest = null;
  });
  return verifyUserPendingRequest;
};

const getKeyMessage = (message): string => {
  switch (message) {
    case 'TOKEN NO LONGER VALID':
      return 'DASHBOARD_SETUP_ACCOUNT_INVALID_LINK';
    default:
      return 'DASHBOARD_ENUM_ERROR_MESSAGE_UNKNOWN';
  }
};

const VerifyNewMemberPage: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyle();
  const [token, setToken] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const query = useQuerySearch();
  const history = useHistory();

  useEffect(() => {
    // Check if a token is already in the local storage
    const storedToken = window.localStorage.getItem('verification-token');
    if (storedToken) {
      setToken(storedToken);
      setIsLoading(false);
    } else {
      verifyUserWithCooldown(query.get('token'))
        .then((response) => {
          setToken(response['token']);
          window.localStorage.setItem('verification-token', response['token']);
          setIsLoading(false);
        })
        .catch((error) => {
          if (error?.message === 'VERIFY_USER_COOLDOWN_ACTIVE') {
            return;
          }

          new Promise((resolve) => setTimeout(resolve, 1000)).then(() => {
            const raceToke = window.localStorage.getItem('verification-token');
            if (!raceToke) {
              history.replace('/verifyEmail');
            } else {
              setToken(storedToken);
              setIsLoading(false);
            }
          });
        });
    }
  }, []);

  const onSubmit = async (values): Promise<void> => {
    try {
      setIsSubmitting(true);
      window.localStorage.removeItem('verification-token');
      const { firstName, lastName } = values;
      await AuthService.setupAccount({ firstName, lastName }, token ? token : query.get('token'));
      setIsVerified(true);
    } catch (error) {
      showSnackbar({ message: t(getKeyMessage(error?.message?.[0])), type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return !isVerified ? (
    <LayoutImage>
      <View role="main">
        {isLoading ? (
          <Loading
            loading={isLoading}
            testId="verify-new-member-loading"
            isBackdrop={false}
            loadingStyle={{ width: '32px', height: '32px' }}
          />
        ) : (
          <>
            <Typography className={classes.header}>{t('DASHBOARD_ACCOUNT_SETUP_TITLE')}</Typography>
            <Typography className={classes.description}>
              {t('DASHBOARD_ACCOUNT_SETUP_BODY')}
            </Typography>
            <SetupAccountForm onSubmit={onSubmit} isSubmitting={isSubmitting}></SetupAccountForm>
          </>
        )}
      </View>
    </LayoutImage>
  ) : (
    <Container role="main" className={classes.container}>
      <Logo />

      <Title
        className={classes.title}
        color="textPrimary"
        variant="h1"
        title={t('DASHBOARD_ACCOUNT_SETUP_SUCCESS')}
      />

      <Title
        className={classes.title}
        color="textPrimary"
        variant="h1"
        title={t('DASHBOARD_EMAIL_VERIFY_PASSWORD_SENT')}
      />

      <RailzButton
        size="large"
        href="/login"
        target="_self"
        className={classes.button}
        label={t('DASHBOARD_EMAIL_VERIFIED_CTA')}
      />
    </Container>
  );
};

export default VerifyNewMemberPage;
